import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import classNames from "classnames";
import type { IScope } from "reducers/scope";
import { DATATEST } from "constants/integration";
import { IProduct } from "records/item/item";
import { makeStyles } from "@material-ui/styles";
import { themeOptions } from "styles/theme";
import { Theme } from "@material-ui/core";

interface IWholesalePricingCellProps {
    item: IProduct;
    priceFieldStyle: Record<string, string | number>;
    scope: IScope;
    activeCurrency: string;
    shipStart?: number;
}

interface StyleProps {
    cellPositionOffset: number;
}

export const WholesalePricingCell: React.FC<IWholesalePricingCellProps> = ({
    item,
    priceFieldStyle,
    scope,
    activeCurrency,
    shipStart,
}) => {
    const enableStrikethrough = scope.brand.isStrikethroughEnabled(scope);
    const [cellPositionOffset, setCellPositionOffset] = React.useState<number>(0);
    const classes = useStyles({ cellPositionOffset: cellPositionOffset });
    const strikethroughRef = React.useRef<HTMLDivElement>(null);

    const { originalWholesale, minOriginalWholesale, minAdjustedWholesale, adjustedWholesale } =
        item?.getFormattedPrices(activeCurrency, { date: shipStart }, scope) || {
            originalWholesale: "",
            minOriginalWholesale: 0,
            minAdjustedWholesale: 0,
            adjustedWholesale: "",
        };

    React.useLayoutEffect(() => {
        if (strikethroughRef.current) {
            setCellPositionOffset(-strikethroughRef.current.clientHeight / 2 - 2);
        } else {
            setCellPositionOffset(0);
        }
    }, [item, setCellPositionOffset]);

    return (
        <div className={classNames(classes.cellWrapper)} data-test={DATATEST.GALLERY.LIST_VIEW_PRICE_CELL}>
            {item && enableStrikethrough && originalWholesale && minOriginalWholesale > minAdjustedWholesale ? (
                <div ref={strikethroughRef} className={classNames(classes.priceWrapper, classes.strikethrough)}>
                    <span className="priceValue strikethrough" data-test={DATATEST.PRODUCT.PRICES.ORIGINAL_WHOLESALE}>
                        {originalWholesale}
                    </span>
                </div>
            ) : null}
            <div
                className={classNames(classes.priceWrapper, { header: !item })}
                style={priceFieldStyle}
                data-test={DATATEST.PRODUCT.PRICES.WHOLESALE}
            >
                {item ? adjustedWholesale || "--" : formatMessage("Wholesale")}
            </div>
        </div>
    );
};

const useStyles = makeStyles<Theme, StyleProps>({
    cellWrapper: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        position: "relative",
        top: ({ cellPositionOffset }) => `${cellPositionOffset}px`,
    },
    priceWrapper: {
        margin: "0px",
        color: themeOptions.palette.darkerGrey,
        height: "20px",
        fontSize: "12px",
        letterSpacing: "0.2px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        float: "left",
        padding: "0 10px",
    },
    strikethrough: {
        textDecoration: "line-through",
    },
});
